// The roll: founding members and rankings. Visitors see the real board with
// codenames redacted, plus where their own browser logbook would place them.

const $ = (sel: string) => document.querySelector(sel) as HTMLElement;

interface Entry {
  codename: string | null;
  length?: number;
  me: boolean;
  hidden?: boolean;
  caught: number;
  rank: string;
}
interface Row extends Entry {
  position: number;
  founding: boolean;
  sightings: number;
}
interface Founder extends Entry {
  foundingNo: number;
}
interface Board {
  period: "all" | "month";
  open: boolean;
  opensAt: number;
  signedIn: boolean;
  stats: {
    members: number;
    foundingTaken: number;
    foundingPlaces: number;
    verifiedThisWeek: number;
    satellitesCaught: number;
    rarestThisMonth: string | null;
  };
  founders: Founder[];
  rows: Row[];
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

function prettyName(name: string): string {
  if (/^ISS\b/.test(name)) return "the International Space Station";
  if (/^CSS\b/.test(name)) return "the Tiangong space station";
  if (/^HST\b/.test(name)) return "the Hubble Space Telescope";
  return name.replace(/\s+R\/B$/, " rocket body");
}

/** Satellites caught in this browser before joining. */
function localCaught(): number {
  try {
    const log = JSON.parse(localStorage.getItem("sh.spotted.v1") || "[]");
    return Array.isArray(log) ? new Set(log.map((s: any) => s.norad)).size : 0;
  } catch {
    return 0;
  }
}

function nameCell(e: Entry): string {
  if (e.codename) return `<span class="roll-name">${esc(e.codename)}</span>${e.me ? ' <span class="roll-you">you</span>' : ""}`;
  const w = Math.max(6, Math.min(22, e.length ?? 12));
  const label = e.hidden ? "A member who keeps their codename private" : "Codename visible to members";
  return `<span class="redacted" style="width:${w}ch" role="img" aria-label="${label}"></span>`;
}

function plural(n: number, one: string, many: string) {
  return `${n} ${n === 1 ? one : many}`;
}

function renderStats(b: Board) {
  const s = b.stats;
  const items: [string, string][] = [
    ["Members", String(s.members)],
    ["Founding places taken", `${s.foundingTaken} of ${s.foundingPlaces}`],
    ["Confirmed sightings this week", String(s.verifiedThisWeek)],
  ];
  if (s.rarestThisMonth) items.push(["Rarest catch this month", prettyName(s.rarestThisMonth)]);
  $("#roll-stats").innerHTML = items.map(([k, v]) => `<div><dt>${k}</dt><dd>${esc(v)}</dd></div>`).join("");
}

function renderInvite(b: Board) {
  const box = $("#roll-invite");
  if (b.signedIn) {
    box.hidden = true;
    return;
  }
  box.hidden = false;
  const mine = localCaught();
  const left = b.stats.foundingPlaces - b.stats.foundingTaken;
  const founding = left > 0 ? ` ${plural(left, "founding place is", "founding places are")} still open.` : "";
  let text: string;
  if (mine > 0) {
    // Position among members with this many or more different satellites.
    const pool = b.open ? b.rows : b.founders;
    const ahead = pool.filter((r) => r.caught > mine).length;
    text = `With the ${plural(mine, "satellite", "different satellites")} in your logbook, you'd stand at <strong>No. ${ahead + 1}</strong> on the roll. Join to claim your place.${founding}`;
  } else {
    text = `Catch your first satellite and join to take your place on the roll.${founding}`;
  }
  $("#roll-invite-text").innerHTML = text;
}

function renderFounders(b: Board) {
  const sec = $("#roll-founders");
  sec.hidden = b.open || !b.founders.length;
  if (sec.hidden) return;
  const toGo = b.opensAt - b.stats.members;
  $("#founders-note").textContent =
    `The first ${b.stats.foundingPlaces} members carry a founding mark for good. ` +
    (toGo > 0 ? `The full rankings open when ${b.opensAt} members are on the roll: ${plural(toGo, "more is", "more are")} needed.` : "");
  $("#founders-list").innerHTML = b.founders
    .map(
      (f) => `<li class="roll-row${f.me ? " me" : ""}">
        <span class="roll-pos">No. ${f.foundingNo}</span>
        <span class="roll-who">${nameCell(f)}</span>
        <span class="roll-rank">${esc(f.rank)}</span>
        <span class="roll-caught">${plural(f.caught, "satellite", "satellites")}</span>
      </li>`,
    )
    .join("");
}

function renderBoard(b: Board) {
  const sec = $("#roll-board");
  sec.hidden = !b.open;
  if (sec.hidden) return;
  const list = $("#board-list");
  if (!b.rows.length) {
    list.innerHTML = `<li class="roll-empty">${
      b.period === "month" ? "No confirmed sightings yet this month. The first clear night decides it." : "No confirmed sightings yet."
    }</li>`;
    return;
  }
  list.innerHTML = b.rows
    .map(
      (r) => `<li class="roll-row${r.me ? " me" : ""}">
        <span class="roll-pos">${r.position}</span>
        <span class="roll-who">${nameCell(r)}${r.founding ? ' <span class="roll-founding" title="Founding member">✦</span>' : ""}</span>
        <span class="roll-rank">${esc(r.rank)}</span>
        <span class="roll-caught">${plural(r.caught, "satellite", "satellites")}</span>
      </li>`,
    )
    .join("");
}

async function load(period: "all" | "month") {
  try {
    const r = await fetch(`/api/board?period=${period}`, { credentials: "same-origin" });
    if (!r.ok) throw new Error();
    const b: Board = await r.json();
    renderStats(b);
    renderInvite(b);
    renderFounders(b);
    renderBoard(b);
    const st = $("#status");
    st.hidden = true;
  } catch {
    const st = $("#status");
    st.textContent = "Couldn't load the roll right now. Reload the page to try again.";
    st.classList.add("error");
    st.hidden = false;
  }
}

for (const tab of document.querySelectorAll<HTMLButtonElement>(".tabs [role=tab]")) {
  tab.addEventListener("click", () => {
    for (const t of document.querySelectorAll(".tabs [role=tab]")) t.setAttribute("aria-selected", String(t === tab));
    load(tab.dataset.period as "all" | "month");
  });
}

load("all");

export {};
